<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const store = useStore();
const { t } = useI18n();

const bases = useMapGetter('captainKnowledgeBases/getRecords');
const uiFlags = useMapGetter('captainKnowledgeBases/getUIFlags');
const assistants = useMapGetter('captainAssistants/getRecords');

const searchQuery = ref('');
const selectedBase = ref({});
const form = ref({ name: '', description: '' });
const formDialogRef = ref(null);
const deleteDialogRef = ref(null);

const tableHeaders = computed(() => [
  t('KNOWLEDGE_BASES.TABLE.BASE'),
  t('KNOWLEDGE_BASES.TABLE.ANSWERS'),
  t('KNOWLEDGE_BASES.TABLE.DOCUMENTS'),
  t('KNOWLEDGE_BASES.TABLE.READERS'),
  t('KNOWLEDGE_BASES.TABLE.ACTIONS'),
]);

const assistantNames = ids =>
  (ids || [])
    .map(id => assistants.value.find(item => item.id === id)?.name)
    .filter(Boolean);

const rows = computed(() => {
  const query = searchQuery.value.trim();
  const list = query
    ? picoSearch(bases.value, query, ['name', 'description'])
    : bases.value;
  return list.map(base => ({
    ...base,
    readers: assistantNames(base.assistant_ids),
  }));
});

const isEditing = computed(() => Boolean(selectedBase.value.id));

const openCreate = () => {
  selectedBase.value = {};
  form.value = { name: '', description: '' };
  formDialogRef.value.open();
};

const openEdit = base => {
  selectedBase.value = base;
  form.value = { name: base.name, description: base.description || '' };
  formDialogRef.value.open();
};

const openDelete = base => {
  selectedBase.value = base;
  deleteDialogRef.value.open();
};

const submitForm = async () => {
  if (!form.value.name.trim()) return;

  const action = isEditing.value ? 'update' : 'create';
  const payload = isEditing.value
    ? { id: selectedBase.value.id, ...form.value }
    : form.value;

  try {
    await store.dispatch(`captainKnowledgeBases/${action}`, payload);
    useAlert(
      t(
        isEditing.value
          ? 'KNOWLEDGE_BASES.EDIT.SUCCESS'
          : 'KNOWLEDGE_BASES.ADD.SUCCESS'
      )
    );
    formDialogRef.value.close();
  } catch (error) {
    useAlert(t('KNOWLEDGE_BASES.ERROR'));
  }
};

const confirmDelete = async () => {
  try {
    await store.dispatch('captainKnowledgeBases/delete', selectedBase.value.id);
    useAlert(t('KNOWLEDGE_BASES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('KNOWLEDGE_BASES.ERROR'));
  }
};

onMounted(() => {
  store.dispatch('captainKnowledgeBases/get');
  store.dispatch('captainAssistants/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="t('KNOWLEDGE_BASES.LOADING')"
    :no-records-found="!bases.length"
    :no-records-message="t('KNOWLEDGE_BASES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('KNOWLEDGE_BASES.HEADER')"
        :description="t('KNOWLEDGE_BASES.DESCRIPTION')"
        :search-placeholder="t('KNOWLEDGE_BASES.SEARCH_PLACEHOLDER')"
      >
        <template #actions>
          <Button
            :label="t('KNOWLEDGE_BASES.ADD.TITLE')"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="rows"
        :no-data-message="t('KNOWLEDGE_BASES.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="base in items" :key="base.id" :item="base">
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex flex-col min-w-0">
                  <span class="truncate text-body-main text-n-slate-12">
                    {{ base.name }}
                  </span>
                  <span class="block truncate text-body-main text-n-slate-11">
                    {{ base.description }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell class="w-28">
                <span class="text-body-main text-n-slate-11">
                  {{ base.responses_count }}
                </span>
              </BaseTableCell>

              <BaseTableCell class="w-28">
                <span class="text-body-main text-n-slate-11">
                  {{ base.documents_count }}
                </span>
              </BaseTableCell>

              <BaseTableCell class="w-64 max-w-64">
                <div v-if="base.readers.length" class="flex flex-wrap gap-1">
                  <span
                    v-for="reader in base.readers"
                    :key="reader"
                    class="px-2 py-0.5 text-xs rounded-md text-n-slate-12 bg-n-alpha-2"
                  >
                    {{ reader }}
                  </span>
                </div>
                <span v-else class="text-body-main text-n-amber-11">
                  {{ t('KNOWLEDGE_BASES.NO_READER') }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-24">
                <div class="flex justify-end gap-3 shrink-0">
                  <Button
                    v-tooltip.top="t('KNOWLEDGE_BASES.EDIT.TITLE')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEdit(base)"
                  />
                  <Button
                    v-tooltip.top="t('KNOWLEDGE_BASES.DELETE.TITLE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDelete(base)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="formDialogRef"
      type="edit"
      :title="
        isEditing
          ? t('KNOWLEDGE_BASES.EDIT.TITLE')
          : t('KNOWLEDGE_BASES.ADD.TITLE')
      "
      :confirm-button-label="
        isEditing
          ? t('KNOWLEDGE_BASES.EDIT.SUBMIT')
          : t('KNOWLEDGE_BASES.ADD.SUBMIT')
      "
      :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
      @confirm="submitForm"
    >
      <div class="flex flex-col gap-4">
        <Input
          id="knowledge-base-name"
          v-model="form.name"
          :label="t('KNOWLEDGE_BASES.FORM.NAME')"
          :placeholder="t('KNOWLEDGE_BASES.FORM.NAME_PLACEHOLDER')"
        />
        <TextArea
          id="knowledge-base-description"
          v-model="form.description"
          :label="t('KNOWLEDGE_BASES.FORM.DESCRIPTION')"
          :placeholder="t('KNOWLEDGE_BASES.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('KNOWLEDGE_BASES.DELETE.TITLE')"
      :description="
        selectedBase.assistant_ids?.length
          ? t('KNOWLEDGE_BASES.DELETE.SHARED_CONFIRM', {
              name: selectedBase.name,
              count: selectedBase.assistant_ids.length,
            })
          : t('KNOWLEDGE_BASES.DELETE.CONFIRM', { name: selectedBase.name })
      "
      :is-loading="uiFlags.deletingItem"
      :confirm-button-label="t('KNOWLEDGE_BASES.DELETE.SUBMIT')"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
